/**
 * Shared region/division constants used across the application.
 * Maps counties to divisions and defines region boundaries.
 */

// County name → division mapping (case-insensitive matching at usage sites)
export const COUNTY_DIVISION_MAP = {
  // Dallas Region — Texas
  "Dallas County": "Dallas Region",
  "Collin County": "Dallas Region",
  "Denton County": "Dallas Region",
  "Tarrant County": "Dallas Region",
  "Grayson County": "Dallas Region",
  // Capitol Region — Texas
  "Travis County": "Capitol Region",
  "Williamson County": "Capitol Region",
  "Bell County": "Capitol Region",
  // Choctaw Region — Oklahoma
  "McCurtain County": "Choctaw Region",
  "Choctaw County": "Choctaw Region",
  // Palmetto Region — South Carolina
  "Sumter County": "Palmetto Region",
  "Clarendon County": "Palmetto Region",
  "Florence County": "Palmetto Region",
  "Richland County": "Palmetto Region",
  "Lexington County": "Palmetto Region",
};

// State name → division mapping (entire-state coverage — checked when county doesn't match above)
export const STATE_DIVISION_MAP = {
};

// FIPS code → { name, region id } for map rendering
export const COUNTY_FIPS = {
  // Dallas Region — Texas
  "48113": { name: "Dallas County", region: "dallas" },
  "48085": { name: "Collin County", region: "dallas" },
  "48121": { name: "Denton County", region: "dallas" },
  "48439": { name: "Tarrant County", region: "dallas" },
  "48181": { name: "Grayson County", region: "dallas" },
  // Capitol Region — Texas
  "48453": { name: "Travis County", region: "austin" },
  "48491": { name: "Williamson County", region: "austin" },
  "48027": { name: "Bell County", region: "austin" },
  // Choctaw Region — Oklahoma
  "40089": { name: "McCurtain County", region: "choctaw" },
  "40023": { name: "Choctaw County", region: "choctaw" },
  // Palmetto Region — South Carolina (specific counties)
  "45085": { name: "Sumter County", region: "palmetto" },
  "45027": { name: "Clarendon County", region: "palmetto" },
  "45041": { name: "Florence County", region: "palmetto" },
  "45079": { name: "Richland County", region: "palmetto" },
  "45063": { name: "Lexington County", region: "palmetto" },
};

// Full region definitions
export const REGIONS = [
  {
    id: "dallas",
    name: "Dallas Region",
    division: "Dallas Region",
    counties: ["Dallas County", "Collin County", "Denton County", "Tarrant County", "Grayson County"],
    fipsCodes: ["48113", "48085", "48121", "48439", "48181"],
    color: "#2563eb",
    center: [-96.8, 33.1],
    leadRangerId: "261",
  },
  {
    id: "austin",
    name: "Capitol Region",
    division: "Capitol Region",
    counties: ["Travis County", "Williamson County", "Bell County"],
    fipsCodes: ["48453", "48491", "48027"],
    color: "#16a34a",
    center: [-97.75, 30.65],
    leadRangerId: "261",
  },
  {
    id: "choctaw",
    name: "Choctaw Region",
    division: "Choctaw Region",
    counties: ["McCurtain County", "Choctaw County"],
    fipsCodes: ["40089", "40023"],
    color: "#d97706",
    center: [-95.0, 34.0],
    leadRangerId: "261",
  },
  {
    id: "palmetto",
    name: "Palmetto Region",
    division: "Palmetto Region",
    state: "South Carolina",
    counties: ["Sumter County", "Clarendon County", "Florence County", "Richland County", "Lexington County"],
    fipsCodes: ["45085", "45027", "45041", "45079", "45063"],
    color: "#6d28d9",
    center: [-80.4, 33.9],
    leadRangerId: "261",
  },
];

// Default division when GPS resolves to a non-serviced county
export const TRAVEL_DIVISION = "Travel Division";

// All known valid divisions
export const VALID_DIVISIONS = [
  "Dallas Region",
  "Capitol Region",
  "Choctaw Region",
  "Palmetto Region",
  TRAVEL_DIVISION,
];

// Division options for onboarding and profile editing
export const DIVISION_OPTIONS = [
  { value: "Capitol Region", label: "Capitol Region" },
  { value: "Dallas Region", label: "Dallas Region" },
  { value: "Choctaw Region", label: "Choctaw Region" },
  { value: "Palmetto Region", label: "Palmetto Region" },
  { value: TRAVEL_DIVISION, label: "Travel Division" },
];

/**
 * Resolve a county name (from Nominatim reverse-geocode) to a division.
 * First checks county-level matches, then falls back to state-level matching
 * (e.g. any county in South Carolina → Palmetto Region).
 * @param {string|null|undefined} countyName - The county name from geocoding (e.g. "Dallas County")
 * @param {string|null|undefined} stateName - The state name from geocoding (e.g. "South Carolina")
 * @returns {string} The resolved division
 */
export function resolveDivisionFromCounty(countyName, stateName) {
  if (!countyName && !stateName) return TRAVEL_DIVISION;

  // --- County-level matching (highest priority) ---
  if (countyName) {
    const normalized = countyName.trim();

    // Direct match first
    if (COUNTY_DIVISION_MAP[normalized]) {
      return COUNTY_DIVISION_MAP[normalized];
    }

    // Try appending " County" if not already present
    if (!normalized.toLowerCase().endsWith(" county")) {
      const withCounty = `${normalized} County`;
      if (COUNTY_DIVISION_MAP[withCounty]) {
        return COUNTY_DIVISION_MAP[withCounty];
      }
    }

    // Case-insensitive fallback
    const lowerNormalized = normalized.toLowerCase();
    for (const [key, value] of Object.entries(COUNTY_DIVISION_MAP)) {
      if (key.toLowerCase() === lowerNormalized) {
        return value;
      }
      if (key.toLowerCase().replace(" county", "") === lowerNormalized.replace(" county", "")) {
        return value;
      }
    }
  }

  // --- State-level matching (catches entire states like SC) ---
  if (stateName) {
    const normalizedState = stateName.trim();
    if (STATE_DIVISION_MAP[normalizedState]) {
      return STATE_DIVISION_MAP[normalizedState];
    }
    // Case-insensitive fallback
    const lowerState = normalizedState.toLowerCase();
    for (const [key, value] of Object.entries(STATE_DIVISION_MAP)) {
      if (key.toLowerCase() === lowerState) {
        return value;
      }
    }
  }

  return TRAVEL_DIVISION;
}
